import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  useTheme,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import AnunciantesService, { 
  Anunciante, 
  AnuncianteCreate, 
  AnuncianteUpdate, 
  AnuncianteFilter 
} from '../../services/anunciantesService';

/**
 * Interface para entidades de referência
 */
interface EntidadeSimples {
  id: number;
  nome: string;
}

const AnunciantesPage: React.FC = () => {
  const theme = useTheme();
  
  // Estado de listagem
  const [anunciantes, setAnunciantes] = useState<Anunciante[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [anunciantesPrincipais, setAnunciantesPrincipais] = useState<EntidadeSimples[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<AnuncianteFilter>({});
  
  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentAnunciante, setCurrentAnunciante] = useState<Anunciante | null>(null);
  const [newAnunciante, setNewAnunciante] = useState<AnuncianteCreate>({ nome: '' });
  const [editAnunciante, setEditAnunciante] = useState<AnuncianteUpdate>({});

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      
      // Buscar anunciantes para uso como "anunciante principal"
      const anunciantesData = await AnunciantesService.getAllAnunciantes();
      setAnunciantesPrincipais(anunciantesData);
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchAnunciantes = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const data = await AnunciantesService.getAnunciantes(filter, skip, limit);
      setAnunciantes(data);
      
      // Obter contagem total para paginação
      const count = await AnunciantesService.getAnunciantesCount(filter);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar anunciantes:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar anunciantes',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar anunciantes ao iniciar a página ou mudar filtros/paginação
    fetchAnunciantes();
    // Carregar referências
    carregarReferencias();
  }, [fetchAnunciantes, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof AnuncianteFilter, value: string | number | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Função para limpar filtro de busca
  const clearSearchFilter = () => {
    setFilter((prev) => ({
      ...prev,
      nome: undefined
    }));
    setPage(0);
  };

  const handleCreateAnunciante = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newAnunciante.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do anunciante é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await AnunciantesService.createAnunciante(newAnunciante);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Anunciante criado com sucesso',
        severity: 'success',
      });
      
      // Limpar formulário
      setNewAnunciante({ nome: '' });
      
      // Recarregar dados
      fetchAnunciantes();
      
      // Atualizar anunciantes principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao criar anunciante:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar anunciante',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (anunciante: Anunciante) => {
    setCurrentAnunciante(anunciante);
    // Inicializar dados para edição
    setEditAnunciante({
      nome: anunciante.nome,
      principal_id: anunciante.principal_id
    });
    
    setOpenEditDialog(true);
  };

  const handleEditAnunciante = async () => {
    if (!currentAnunciante) return;

    try {
      // Verificar campos obrigatórios
      if (!editAnunciante.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do anunciante é obrigatório',
          severity: 'error',
        });
        return;
      }

      await AnunciantesService.updateAnunciante(currentAnunciante.id, editAnunciante);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Anunciante atualizado com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentAnunciante(null);
      setEditAnunciante({});
      
      // Recarregar dados
      fetchAnunciantes();
      
      // Atualizar anunciantes principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao atualizar anunciante:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar anunciante',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (anunciante: Anunciante) => {
    setCurrentAnunciante(anunciante);
    setOpenDeleteDialog(true);
  };

  const handleDeleteAnunciante = async () => {
    if (!currentAnunciante) return;

    try {
      await AnunciantesService.deleteAnunciante(currentAnunciante.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Anunciante excluído com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentAnunciante(null);
      
      // Recarregar dados
      fetchAnunciantes();
      
      // Atualizar anunciantes principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao excluir anunciante:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir anunciante',
        severity: 'error',
      });
    }
  };

  // Handler para alterações em selects
  const handleSelectChange = (event: SelectChangeEvent<number | string | undefined>, setter: Function) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setter((prev: any) => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <SidebarLayout>
      <Box p={3}>
        <Typography variant="h4" gutterBottom display="flex" alignItems="center">
          <BusinessIcon sx={{ mr: 2 }} />
          Gerenciamento de Anunciantes
        </Typography>
        
        {/* Área de filtros */}
        <Paper 
          elevation={2} 
          sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.default }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar por nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filter.nome ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={clearSearchFilter}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="principal-filter-label">Anunciante Principal</InputLabel>
                <Select
                  labelId="principal-filter-label"
                  label="Anunciante Principal"
                  name="principal_id"
                  value={filter.principal_id || ''}
                  onChange={(e) => handleFilterChange('principal_id', e.target.value === '' ? undefined : Number(e.target.value))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {anunciantesPrincipais.map((anunciante) => (
                    <MenuItem key={anunciante.id} value={anunciante.id}>
                      {anunciante.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{ height: '100%' }}
              >
                Novo Anunciante
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Anunciantes */}
        <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Anunciante Principal</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : anunciantes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhum anunciante encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  anunciantes.map((anunciante) => (
                    <TableRow key={anunciante.id} hover>
                      <TableCell>{anunciante.id}</TableCell>
                      <TableCell>{anunciante.nome}</TableCell>
                      <TableCell>
                        {anunciantesPrincipais.find(a => a.id === anunciante.principal_id)?.nome || '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(anunciante)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(anunciante)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo Anunciante</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newAnunciante.nome || ''}
                onChange={(e) => setNewAnunciante({ ...newAnunciante, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-principal-label">Anunciante Principal</InputLabel>
                <Select
                  labelId="new-principal-label"
                  label="Anunciante Principal"
                  name="principal_id"
                  value={newAnunciante.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewAnunciante)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {anunciantesPrincipais.map((anunciante) => (
                    <MenuItem key={anunciante.id} value={anunciante.id}>
                      {anunciante.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateAnunciante}
            disabled={!newAnunciante.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar Anunciante</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={editAnunciante.nome || ''}
                onChange={(e) => setEditAnunciante({ ...editAnunciante, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-principal-label">Anunciante Principal</InputLabel>
                <Select
                  labelId="edit-principal-label"
                  label="Anunciante Principal"
                  name="principal_id"
                  value={editAnunciante.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditAnunciante)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {anunciantesPrincipais.map((anunciante) => (
                    <MenuItem key={anunciante.id} value={anunciante.id}>
                      {anunciante.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEditAnunciante}
            disabled={!editAnunciante.nome}
          >
            Atualizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza de que deseja excluir o anunciante "{currentAnunciante?.nome}"? 
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button color="error" onClick={handleDeleteAnunciante}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default AnunciantesPage;
